import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Button,
  Accordion,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCircle,
  FaSquare,
  FaCheckCircle,
  FaClock,
  FaWeightHanging,
  FaNetworkWired,
  FaLock,
} from "react-icons/fa";
import { BiSolidUpArrow } from "react-icons/bi";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "../audit_style.css";
import { useAuditStore } from "../store/useAuditStore";

interface Detail {
  label: string;
  value: number;
  items?: string[];
}

interface Heading {
  label: string;
  value: number;
  items: string[];
}

interface Metric {
  name: string;
  status: string;
  description: string;
  details?: Detail[];
  headings?: Heading[];
}

interface Section {
  title: string;
  metrics: Metric[];
}

interface OverviewData {
  websiteScore: number;
  siteTitle: string;
  subtitle: string;
  description: string;
  url: string;
  criticalCount: number;
  warningCount: number;
  noticesCount: number;
  passedCount: number;
  criticalPercentage: string;
  warningPercentage: string;
  noticesPercentage: string;
  passedPercentage: string;
  loadTime: string;
  fileSize: string;
  resources: number;
  secure: string;
  sections: Section[];
}

const OverviewPage: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { audits } = useAuditStore();
  const {
    siteTitle,
    websiteScore,
    criticalCollected,
    warningCollected,
    noticesCollected,
    passedCollected,
    criticalPercentage,
    warningPercentage,
    noticesPercentage,
    passedPercentage,
  } = state || {};

  const overviewData: OverviewData = {
    websiteScore: websiteScore || 86,
    siteTitle: siteTitle || "22plasticsurgery.com",
    subtitle: "EAR SURGERY Archives - 22 Plastic Surgery",
    description:
      "Read our informative blogs on ear surgery tips and advice at 22 Plastic Surgery. Get valuable insights into ear surgery procedures and recovery process.",
    url: "https://22plasticsurgery.com/category/ear-surgery/",
    criticalCount:
      criticalCollected?.reduce(
        (sum: number, issue: { value: number }) => sum + issue.value,
        0
      ) || 12,
    warningCount:
      warningCollected?.reduce(
        (sum: number, issue: { value: number }) => sum + issue.value,
        0
      ) || 10,
    noticesCount:
      noticesCollected?.reduce(
        (sum: number, issue: { value: number }) => sum + issue.value,
        0
      ) || 8,
    passedCount:
      passedCollected?.reduce(
        (sum: number, issue: { value: number }) => sum + issue.value,
        0
      ) || 30,
    criticalPercentage: criticalPercentage || "28.57",
    warningPercentage: warningPercentage || "27.27",
    noticesPercentage: noticesPercentage || "18.18",
    passedPercentage: passedPercentage || "54.55",
    loadTime: "0.25 seconds",
    fileSize: "31.06 kB",
    resources: 84,
    secure: "Secure",
    sections: [
      {
        title: "SEO",
        metrics: [
          {
            name: "Title",
            status: "Passed",
            description: "The title tag is perfect.",
          },
          {
            name: "Meta description",
            status: "Criticals",
            description: "The meta description tag is good.",
          },
          {
            name: "Headings",
            status: "Passed",
            description: "The headings on the webpage are properly set.",
            headings: [
              { label: "H1", value: 1, items: ["EAR SURGERY"] },
              {
                label: "H2",
                value: 5,
                items: [
                  "Subscribe To Our Newsletter",
                  "Our Locations",
                  "Our Links",
                  "Stay Connected",
                  "Join Our Email List For Specials!",
                ],
              },
              { label: "H3", value: 1, items: ["Read our reviews"] },
              { label: "H4", value: 1, items: ["Earlobe Repair Surgery"] },
              {
                label: "H5",
                value: 2,
                items: ["22 Plastic Surgery", "22 Spa MD"],
              },
            ],
          },
          {
            name: "Content keywords",
            status: "Criticals",
            description: "All images have alt attributes.",
          },
          {
            name: "Image keywords",
            status: "Passed",
            description: "All images have alt attributes.",
          },
          {
            name: "SEO friendly URL",
            status: "Notices",
            description: "The URL is SEO friendly.",
          },
          {
            name: "404 page",
            status: "Passed",
            description: "The website has 404 error pages.",
          },
          {
            name: "Robots.txt",
            status: "Criticals",
            description: "The website has a robots.txt file.",
          },
          {
            name: "Noindex",
            status: "Passed",
            description: "The webpage does not have a noindex tag set.",
          },
          {
            name: "In-page links",
            status: "Warnings",
            description: "The number of links on the webpage is okay.",
            headings: [
              {
                label: "Internals",
                value: 78,
                items: [
                  "/about",
                  "/contact",
                  "/services",
                  "/ear-surgery",
                  "/blog",
                  "/testimonials",
                  "/faq",
                  "/privacy-policy",
                ],
              },
              {
                label: "Externals",
                value: 0,
                items: [],
              },
            ],
          },
          {
            name: "Language",
            status: "Passed",
            description: "The webpage has the language declared.",
          },
          {
            name: "Favicon",
            status: "Passed",
            description: "The webpage has a favicon.",
          },
        ],
      },
      {
        title: "Performance",
        metrics: [
          {
            name: "Text compression",
            status: "Criticals",
            description: "The HTML file is not compressed.",
          },
          {
            name: "Load time",
            status: "Passed",
            description: "The webpage loaded in 0.25 seconds.",
          },
          {
            name: "Page requests",
            status: "Passed",
            description: "The webpage is 31.06 KB.",
          },
          {
            name: "HTTP requests",
            status: "Warnings",
            description: "The webpage makes more than 50 HTTP requests.",
            headings: [
              {
                label: "CSS",
                value: 2,
                items: ["/assets/css/main.css", "/assets/css/responsive.css"],
              },
              {
                label: "Images",
                value: 46,
                items: [
                  "/assets/img/logo.png",
                  "/assets/img/banner.jpg",
                  "/assets/img/team/doctor1.jpg",
                ],
              },
              {
                label: "JavaScript",
                value: 10,
                items: [
                  "/assets/js/main.js",
                  "/assets/js/analytics.js",
                  "/assets/js/plugins.js",
                ],
              },
            ],
          },
          {
            name: "Images",
            status: "Warnings",
            description:
              "There are 46 images that are not using the AVIF, WebP format.",
          },
          {
            name: "Javascript defer",
            status: "Warnings",
            description:
              "There are 10 Javascript resources without the defer attribute.",
          },
          {
            name: "DOM size",
            status: "Notices",
            description: "The DOM size is optimal.",
          },
          {
            name: "DOCTYPE",
            status: "Passed",
            description: "The webpage has the DOCTYPE declaration tag set.",
          },
        ],
      },
      {
        title: "Miscellaneous",
        metrics: [
          {
            name: "Structured data",
            status: "Passed",
            description: "The webpage has structured data.",
            headings: [
              {
                label: "Open Graph",
                value: 6,
                items: [
                  "og:title",
                  "og:description",
                  "og:url",
                  "og:site_name",
                  "og:image",
                  "og:type",
                ],
              },
              {
                label: "Twitter",
                value: 3,
                items: ["twitter:card", "twitter:title", "twitter:description"],
              },
              {
                label: "Schema",
                value: 1,
                items: ["WebPage schema markup"],
              },
            ],
          },
          {
            name: "Meta viewport",
            status: "Passed",
            description: "The webpage has a meta viewport tag set.",
          },
          {
            name: "Character set",
            status: "Criticals",
            description: "The webpage has the charset value set.",
          },
          {
            name: "Sitemap",
            status: "Passed",
            description: "No sitemap found.",
          },
          {
            name: "Social",
            status: "Passed",
            description: "The webpage has social links.",
            headings: [
              {
                label: "Facebook",
                value: 1,
                items: ["https://facebook.com/22plasticsurgery"],
              },
              {
                label: "LinkedIn",
                value: 1,
                items: ["https://linkedin.com/company/22plasticsurgery"],
              },
              {
                label: "YouTube",
                value: 1,
                items: ["https://youtube.com/22plasticsurgery"],
              },
              {
                label: "Instagram",
                value: 1,
                items: ["https://instagram.com/22plasticsurgery"],
              },
            ],
          },
          {
            name: "Content length",
            status: "Warnings",
            description: "The webpage has 5,022 words.",
          },
          {
            name: "Text to HTML ratio",
            status: "Passed",
            description: "The text to HTML ratio is 24%.",
          },
          {
            name: "Inline CSS",
            status: "Criticals",
            description: "The webpage contains inline CSS.",
          },
          {
            name: "Deprecated HTML",
            status: "Passed",
            description: "There are no deprecated HTML tags on the webpage.",
          },
        ],
      },
    ],
  };

  const gradientId = "progressGradient";

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Passed":
        return (
          <FaCheckCircle style={{ color: "#28a745", marginRight: "5px" }} />
        );
      case "Warnings":
        return <FaSquare style={{ color: "#ffc107", marginRight: "5px" }} />;
      case "Notices":
        return <FaCircle style={{ color: "#6c757d", marginRight: "5px" }} />;
      case "Criticals":
        return (
          <BiSolidUpArrow style={{ color: "#dc3545", marginRight: "5px" }} />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      {/* <Button onClick={() => navigate('/seo-website-audit')} className="mb-3">
        Back to Scorecard
      </Button> */}

      <Card
        className="p-4 mt-3"
        style={{
          maxWidth: "100%",
          boxShadow:
            "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
        }}
      >
        <Row>
          <Col>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "2rem",
              }}
            >
              <div
                style={{
                  width: 150,
                  height: 150,
                  marginBottom: "3rem",
                }}
              >
                <svg style={{ height: 0 }}>
                  <defs>
                    <linearGradient
                      id={gradientId}
                      gradientTransform="rotate(90)"
                    >
                      <stop offset="0%" stopColor="#00FF00" />
                      <stop offset="50%" stopColor="#FFD700" />
                      <stop offset="100%" stopColor="#FF4500" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div style={{ width: "100%" }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "25px",
                    marginBottom: "0.3rem",
                  }}
                >
                  {overviewData.siteTitle}
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 500,
                    marginBottom: "0.3rem",
                  }}
                >
                  {overviewData.subtitle}
                </div>
                <p
                  style={{
                    marginBottom: "0.3rem",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  {overviewData.description}
                </p>
                <a
                  href={overviewData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#327aee",
                    fontSize: "14px",
                    textDecoration: "none",
                  }}
                >
                  {overviewData.url}
                </a>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        className="p-4 mt-3"
        style={{
          maxWidth: "100%",
          boxShadow:
            "rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px",
        }}
      >
        <Row>
          <Col>
            <div
              style={{
                padding: "5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>All Issue</div>
                <div style={{ marginLeft: "auto" }}>100%</div>
              </div>
              <ProgressBar now={100} style={{ height: "7px" }} />
            </div>
          </Col>
          <Col>
            <div
              style={{
                padding: "5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>
                  <BiSolidUpArrow
                    style={{ marginRight: "5px", color: "#dc3545" }}
                  />
                  {overviewData.criticalCount} Criticals
                </div>
                <div style={{ marginLeft: "auto" }}>
                  {overviewData.criticalPercentage}%
                </div>
              </div>
              <ProgressBar
                now={Number(overviewData.criticalPercentage) || 0}
                style={{ height: "7px" }}
                variant="danger"
              />
            </div>
          </Col>
          <Col>
            <div
              style={{
                padding: "5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>
                  <FaSquare style={{ marginRight: "5px", color: "#ffc107" }} />
                  {overviewData.warningCount} Warnings
                </div>
                <div style={{ marginLeft: "auto" }}>
                  {overviewData.warningPercentage}%
                </div>
              </div>
              <ProgressBar
                now={Number(overviewData.warningPercentage) || 0}
                style={{ height: "7px" }}
                variant="warning"
              />
            </div>
          </Col>
          <Col>
            <div
              style={{
                padding: "5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>
                  <FaCircle style={{ marginRight: "5px", color: "#6c757d" }} />
                  {overviewData.noticesCount} Notices
                </div>
                <div style={{ marginLeft: "auto" }}>
                  {overviewData.noticesPercentage}%
                </div>
              </div>
              <ProgressBar
                now={Number(overviewData.noticesPercentage) || 0}
                style={{ height: "7px" }}
                variant="secondary"
              />
            </div>
          </Col>
          <Col>
            <div
              style={{
                padding: "5px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "0.5rem",
                }}
              >
                <div>
                  <FaCheckCircle
                    style={{ marginRight: "5px", color: "#28a745" }}
                  />
                  {overviewData.passedCount} Passed
                </div>
                <div style={{ marginLeft: "auto" }}>
                  {overviewData.passedPercentage}%
                </div>
              </div>
              <ProgressBar
                now={Number(overviewData.passedPercentage) || 0}
                style={{ height: "7px" }}
                variant="success"
              />
            </div>
          </Col>
        </Row>
      </Card>

      <Row className="mt-3">
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <FaClock style={{ marginRight: "5px", color: "#6c757d" }} />
            <span>{overviewData.loadTime}</span>
          </div>
        </Col>
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <FaWeightHanging style={{ marginRight: "5px", color: "#6c757d" }} />
            <span>{overviewData.fileSize}</span>
          </div>
        </Col>
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <FaNetworkWired style={{ marginRight: "5px", color: "#6c757d" }} />
            <span>{overviewData.resources} resources</span>
          </div>
        </Col>
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#f8f9fa",
              padding: "10px",
              borderRadius: "5px",
            }}
          >
            <FaLock style={{ marginRight: "5px", color: "#6c757d" }} />
            <span>{overviewData.secure}</span>
          </div>
        </Col>
      </Row>

      <Accordion defaultActiveKey="0" className="mt-4">
        {overviewData.sections.map((section, sectionIndex) => (
          <Accordion.Item key={sectionIndex} eventKey={sectionIndex.toString()}>
            <Accordion.Header>{section.title}</Accordion.Header>
            <Accordion.Body>
              {section.metrics.map((metric, metricIndex) => (
                <Card
                  key={metricIndex}
                  className="p-3 mb-2"
                  style={{ border: "1px solid #e9ecef" }}
                >
                  <Row>
                    <Col md={3} className="d-flex align-items-center">
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {getStatusIcon(metric.status)}
                        <span>{metric.name}</span>
                      </div>
                    </Col>
                    <Col md={9}>
                      <p>{metric.description}</p>
                      {metric.headings ? (
                        <Accordion>
                          {metric.headings.map((heading, headingIndex) => (
                            <Accordion.Item
                              key={headingIndex}
                              eventKey={headingIndex.toString()}
                            >
                              <Accordion.Header>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <span>{heading.label}</span>
                                  <span
                                    style={{
                                      backgroundColor: "#E3E3E3",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      marginLeft: "10px",
                                      fontSize: "14px",
                                      lineHeight: "1.5",
                                    }}
                                  >
                                    {heading.value}
                                  </span>
                                </div>
                              </Accordion.Header>
                              <Accordion.Body>
                                {heading.items.map((item, itemIndex) => (
                                  <div
                                    key={itemIndex}
                                    style={{
                                      marginLeft: "20px",
                                      marginTop: "5px",
                                    }}
                                  >
                                    {itemIndex + 1}. {item}
                                  </div>
                                ))}
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      ) : null}
                    </Col>
                  </Row>
                </Card>
              ))}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    </Container>
  );
};

export default OverviewPage;
